@let generated = generatedSg();
@let revoking = revokingSg();
@let keys = apiKeys.keysSg();
@let loading = apiKeys.loadingSg();
@let error = apiKeys.errorSg();

<atn-ds-slideout-overlay
  name="API Access"
  [position]="'l'"
  [offsetX]="store.widthSg()"
>
  <div
    class="api-keys__header-actions"
    actions
  >
    <i
      class="atn-icon atn-close atn-red"
      (click)="requestClose()"
      data-test-id="api-keys-close"
    ></i>
  </div>

  <div class="api-keys">
    <p class="api-keys__intro">
      API keys let you call the REST API as yourself, in this zone only. A key
      can never do more than your own permissions allow.
    </p>

    @if (error) {
      <div class="api-keys__banner api-keys__banner--error">
        <i class="atn-icon default atn-warning-triangle"></i>
        <span>{{ error }}</span>
      </div>
    }

    <!-- The one and only chance to copy a newly minted key -->
    @if (generated) {
      <section class="api-keys__reveal">
        <header class="api-keys__reveal-header">
          <i class="atn-icon default atn-warning-triangle"></i>
          <span>Copy this key now — it will not be shown again</span>
        </header>

        <div class="api-keys__reveal-key-row">
          <code
            class="api-keys__reveal-key"
            data-test-id="api-key-plaintext"
            >{{ generated.key }}</code
          >
          <i
            class="atn-icon atn-copy atn-blue"
            (click)="copyGenerated()"
            atnDsTooltip="Copy to clipboard"
            data-test-id="api-key-copy"
          ></i>
        </div>

        <footer class="api-keys__reveal-footer">
          @if (copyFailedSg()) {
            <span class="api-keys__copy-failed">
              Copy blocked — select the key and copy it manually.
            </span>
          } @else if (copiedSg()) {
            <span class="api-keys__copied">
              <i class="atn-icon atn-check atn-blue"></i>
              Copied
            </span>
          }
          <atn-ds-button
            type="secondary"
            (click)="dismissGenerated()"
            data-test-id="api-key-dismiss"
          >
            I've saved it
          </atn-ds-button>
        </footer>
      </section>
    }

    <!--
      Hidden entirely while a generated key is still on screen: generating
      again would overwrite the plaintext the user has not yet saved
    -->
    @if (apiKeys.canGenerateSg() && !generated) {
      @if (generatingSg()) {
        <section class="api-keys__form">
          <atn-ds-text
            label="Name"
            placeholder="What is this key for?"
            [value]="nameSg()"
            (onInput)="nameSg.set($event)"
            data-test-id="api-key-name"
          />

          <div class="api-keys__field">
            <atn-ds-datepicker
              label="Expires on"
              [(value)]="expiresOnSg"
              data-test-id="api-key-expiry"
            />
            <div class="api-keys__field-footer">
              @if (expiryIsPastSg()) {
                <span class="api-keys__field-error">
                  Pick a date in the future.
                </span>
              } @else if (expiresOnSg()) {
                <span
                  class="api-keys__link"
                  (click)="expiresOnSg.set(undefined)"
                >
                  Clear — never expire
                </span>
              } @else {
                <span class="api-keys__hint">
                  Leave empty for a key that never expires.
                </span>
              }
            </div>
          </div>

          <div class="api-keys__form-actions">
            <atn-ds-button
              type="secondary"
              (click)="generatingSg.set(false)"
            >
              Cancel
            </atn-ds-button>
            <atn-ds-button
              [disabled]="!canSubmitSg()"
              [loading]="loading"
              (click)="generate()"
              data-test-id="api-key-generate-submit"
            >
              Generate key
            </atn-ds-button>
          </div>
        </section>
      } @else {
        <atn-ds-button
          (click)="generatingSg.set(true)"
          data-test-id="api-key-generate"
        >
          Generate new key
        </atn-ds-button>
      }
    }

    <div class="api-keys__list">
      @for (key of keys; track key.id) {
        @let status = describeStatus(key);
        <article
          class="api-keys__key"
          [class.api-keys__key--inactive]="status !== 'Active'"
        >
          <div class="api-keys__key-body">
            <div class="api-keys__key-title">
              <span class="api-keys__key-name">
                {{ key.name || 'Unnamed key' }}
              </span>
              <span
                class="api-keys__pill"
                [class.api-keys__pill--active]="status === 'Active'"
                [class.api-keys__pill--expired]="status === 'Expired'"
                [class.api-keys__pill--revoked]="status === 'Revoked'"
              >
                {{ status }}
              </span>
            </div>

            <dl class="api-keys__key-meta">
              <div>
                <dt>Created</dt>
                <dd>{{ key.createdAt | date: 'mediumDate' }}</dd>
              </div>
              <div>
                <dt>Last used</dt>
                <dd>
                  @if (key.lastUsedAt) {
                    {{ key.lastUsedAt | date: 'medium' }}
                  } @else {
                    Never
                  }
                </dd>
              </div>
              <div>
                <dt>Expires</dt>
                <dd>
                  @if (key.expiresAt) {
                    {{ key.expiresAt | date: 'mediumDate' }}
                  } @else {
                    Never
                  }
                </dd>
              </div>
            </dl>
          </div>

          @if (key.enabled) {
            <i
              class="atn-icon atn-trash atn-red"
              (click)="revokingSg.set(key)"
              atnDsTooltip="Revoke this key"
              [attr.data-test-id]="'api-key-revoke-' + key.id"
            ></i>
          }
        </article>
      } @empty {
        @if (!loading) {
          <div class="api-keys__empty">
            You have no API keys in this zone.
          </div>
        }
      }
    </div>
  </div>
</atn-ds-slideout-overlay>

@if (confirmingCloseSg()) {
  <atn-ds-confirmation-overlay
    title="Discard the new key?"
    description="You have not confirmed saving this key yet. Closing now discards it permanently — it cannot be shown again."
    confirmButtonText="Discard"
    cancelButtonText="Keep it open"
    (cancel)="confirmingCloseSg.set(false)"
    (confirm)="confirmClose()"
  ></atn-ds-confirmation-overlay>
}

@if (revoking) {
  <atn-ds-confirmation-overlay
    title="Revoke API Key"
    description="Anything using this key will stop working immediately. This cannot be undone."
    confirmButtonText="Revoke"
    (cancel)="revokingSg.set(undefined)"
    (confirm)="confirmRevoke()"
  >
    <ng-container details>
      <div class="api-keys__revoke-details">
        {{ revoking.name || 'Unnamed key' }}
      </div>
    </ng-container>
  </atn-ds-confirmation-overlay>
}
